import React, { useState, useEffect } from 'react';
import '../styles/SharePrizeModal.css';
import { getActiveFriends } from '../services/friendService';
import { sharePrize } from '../services/prizeShareService';
import { Friend } from '../types/friend';
import { useAuth } from '../context/AuthContext';

interface SharePrizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  prizeId: string;
  prizeName: string;
  onShareSuccess: () => void;
}

const SharePrizeModal: React.FC<SharePrizeModalProps> = ({
  isOpen,
  onClose,
  prizeId,
  prizeName,
  onShareSuccess
}) => {
  const { currentUser } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [selectedFriends, setSelectedFriends] = useState<Set<string>>(new Set());
  const [giftMessage, setGiftMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && currentUser) {
      loadFriends();
    }
  }, [isOpen, currentUser]);

  const loadFriends = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const activeFriends = await getActiveFriends(currentUser.uid);
      setFriends(activeFriends);
      
      if (activeFriends.length === 0) {
        setError('Du har ingen venner at dele med. Tilføj venner via din profil.');
      }
    } catch (error) {
      console.error('Error loading friends:', error);
      setError('Fejl ved indlæsning af venner');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFriend = (friendId: string) => {
    const newSelection = new Set(selectedFriends);
    if (newSelection.has(friendId)) {
      newSelection.delete(friendId);
    } else {
      newSelection.add(friendId);
    }
    setSelectedFriends(newSelection);
    setError('');
  };

  const handleNext = () => {
    if (selectedFriends.size === 0) {
      setError('Vælg mindst én ven at dele med');
      return;
    }
    
    if (giftMessage.length > 80) {
      setError('Besked må ikke være længere end 80 tegn');
      return;
    }
    
    setShowConfirmation(true);
  };

  const handleConfirmShare = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      await sharePrize(
        prizeId,
        currentUser.uid,
        Array.from(selectedFriends),
        giftMessage || undefined
      );
      
      onShareSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error sharing prize:', error);
      setError(error.message || 'Fejl ved deling af præmie');
      setShowConfirmation(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedFriends(new Set());
    setGiftMessage('');
    setShowConfirmation(false);
    setError('');
    onClose();
  };

  const getSelectedFriendNames = (): string => {
    const selectedFriendsList = friends.filter(f => selectedFriends.has(f.id));
    return selectedFriendsList.map(f => f.username).join(', ');
  };

  if (!isOpen) return null;

  return (
    <div className="share-prize-modal-overlay" onClick={handleClose}>
      <div className="share-prize-modal" onClick={(e) => e.stopPropagation()}>
        {!showConfirmation ? (
          <>
            {/* Header */}
            <div className="share-prize-modal-header">
              <h2>Del præmie</h2>
              <button className="share-prize-modal-close" onClick={handleClose}>
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="share-prize-modal-content">
              <div className="share-prize-info">
                <p className="share-prize-title">Du deler: <strong>{prizeName}</strong></p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="share-prize-error">
                  {error}
                </div>
              )}

              {/* Friends List */}
              {loading ? (
                <div className="share-prize-loading">Indlæser venner...</div>
              ) : friends.length > 0 ? (
                <>
                  <div className="share-friends-list">
                    <h3>Vælg venner:</h3>
                    {friends.map((friend) => (
                      <div
                        key={friend.id}
                        className={`share-friend-item ${selectedFriends.has(friend.id) ? 'selected' : ''}`}
                        onClick={() => handleToggleFriend(friend.id)}
                      >
                        <div className="share-friend-info">
                          <span className="share-friend-name">{friend.username}</span>
                          <span className="share-friend-wiinid">({friend.wiinId})</span>
                        </div>
                        <div className="share-friend-checkbox">
                          {selectedFriends.has(friend.id) && (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 8L6 11L13 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Gift Message */}
                  <div className="share-gift-message-section">
                    <label>Besked (valgfri):</label>
                    <textarea
                      placeholder="Skriv en besked til din ven (maks 80 tegn)"
                      value={giftMessage}
                      onChange={(e) => setGiftMessage(e.target.value)}
                      maxLength={80}
                      rows={3}
                      className="share-gift-message-input"
                    />
                    <div className="share-message-counter">
                      {giftMessage.length} / 80
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="share-prize-actions">
                    <button
                      onClick={handleNext}
                      className="share-prize-confirm-btn"
                      disabled={loading || selectedFriends.size === 0}
                    >
                      Næste
                    </button>
                  </div>
                </>
              ) : (
                <div className="share-prize-no-friends">
                  <p>Du har ingen venner at dele med endnu.</p>
                  <p className="share-prize-hint">Gå til din profil for at tilføje venner.</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Confirmation Screen */}
            <div className="share-prize-modal-header">
              <h2>Bekræft</h2>
              <button className="share-prize-modal-close" onClick={handleClose}>
                ✕
              </button>
            </div>

            <div className="share-prize-modal-content">
              <div className="share-prize-confirmation">
                <p className="share-confirmation-question">
                  Vil du sende "<strong>{prizeName}</strong>" til <strong>{getSelectedFriendNames()}</strong>?
                </p>

                {giftMessage && (
                  <div className="share-confirmation-message">
                    <p className="share-message-label">Din besked:</p>
                    <p className="share-message-text">"{giftMessage}"</p>
                  </div>
                )}

                {error && (
                  <div className="share-prize-error">
                    {error}
                  </div>
                )}

                <div className="share-confirmation-actions">
                  <button
                    onClick={handleConfirmShare}
                    className="share-confirm-final-btn"
                    disabled={loading}
                  >
                    {loading ? 'Sender...' : 'Bekræft'}
                  </button>
                  <button
                    onClick={() => setShowConfirmation(false)}
                    className="share-cancel-btn"
                    disabled={loading}
                  >
                    Tilbage
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SharePrizeModal;
